import { Grid } from '@material-ui/core';
import React from 'react';

import { StoreIcon } from 'src/components/icons/StoreIcon';
import { FlagsContext } from 'src/context/flagsContext';
import history from 'src/history';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { PageLayout, PageShell } from 'src/legacy/components/Layout';
import Layout from 'src/legacy/components/Layout/Layout';
import { LoadingWrapper } from 'src/legacy/components/Loading/LoadingWrapper';
import { PageToolbar, ShowSidebarConfig } from 'src/legacy/components/UI';
import { TableToolbar } from 'src/legacy/components/UI/Pages/TableToolbar';
import { StandardPage } from 'src/legacy/components/UI/StandardPage/StandardPage';
import { ProductStorePage } from 'src/routes/billing/store/client/components/ProductStore';
import { ProductStoreVisibility } from 'src/routes/billing/store/schema';
import {
  useGetProductStoresQuery,
  useGetRichProductStoreByShortIdQuery,
} from 'src/services/api/productStoreApi';

export const ProductStoreClientPage = () => {
  const { data: productStores = [], isLoading: areProductStoresLoading } =
    useGetProductStoresQuery();
  const isExtraSmallViewport = useMediaQuery('lg', 'max');
  const { EnableNewAppLayout } = React.useContext(FlagsContext);

  const productStoreId = productStores.at(0)?.ref || '';
  const { data: productStore, isLoading: isLoadingRichProductStore } =
    useGetRichProductStoreByShortIdQuery(
      { shortId: productStoreId },
      {
        skip: !productStoreId,
      },
    );

  const { instance: isLoggedInUser } = useAppSelector((state) => state.user);

  const onClickPurchase = (productId: string, priceId: string) => {
    if (productStoreId) {
      history.push(
        `/store/${productStoreId}/pay?productId=${productId}&priceId=${priceId}`,
      );
    }
  };

  const displayBars = Boolean(isLoggedInUser);

  const headerContent = displayBars ? (
    <TableToolbar>
      <PageToolbar title={productStore?.displayName} actionButtons={null} />
    </TableToolbar>
  ) : (
    <PageToolbar title={productStore?.displayName} actionButtons={null} />
  );

  const pageContent = (
    <LoadingWrapper
      isLoading={areProductStoresLoading || isLoadingRichProductStore}
      wrapperProps={{ height: '100%' }}
      hideContentWhileLoading
    >
      <Grid
        container
        className="h-full justify-center lg:@container grid-container sm:overflow-hidden sm:overflow-y-scroll overflow-scroll block sm:flex pb-[60px] sm:pb-0 px-0 sm:px-3"
      >
        {productStore ? (
          <div className="w-full max-w-[1440px]">
            <ProductStorePage
              productStore={productStore}
              onClickPurchase={onClickPurchase}
            />
          </div>
        ) : (
          <div className="flex justify-center items-center h-full px-4">
            <StandardPage
              topIcon={<StoreIcon className="w-[25px] h-[25px] mt-[2px]" />}
              titleForClient={
                productStoreId
                  ? 'The store is not available.'
                  : 'Store not found'
              }
              descForClient="Please contact the business directly for assistance or inquiries."
            />
          </div>
        )}
      </Grid>
    </LoadingWrapper>
  );

  if (EnableNewAppLayout) {
    // show header when the store is visible
    // (Public/All/ClientUser) or when a logged-in CU is on a small viewport.
    // For "Store not found" (productStore is undefined), hide the header
    // entirely to avoid showing an empty divider.
    const storeVisibility = productStore?.productStorePreferences.visibility;
    const showHeader =
      (displayBars && isExtraSmallViewport) ||
      storeVisibility === ProductStoreVisibility.Public ||
      storeVisibility === ProductStoreVisibility.All ||
      storeVisibility === ProductStoreVisibility.ClientUser;

    if (showHeader) {
      return (
        <PageShell
          header={headerContent}
          access="allowClientStore"
          showSpace={false}
          showSidebar={displayBars}
          unauthAccess
        >
          {pageContent}
        </PageShell>
      );
    }
    // Store not found or not visible: hide the navbar header (no divider)
    // but keep the sidebar for logged-in users.
    return (
      <PageShell
        variant="fullbleed"
        showSidebar={displayBars}
        access="allowClientStore"
        unauthAccess
      >
        {pageContent}
      </PageShell>
    );
  }

  return (
    <Layout
      hasSidebar={displayBars}
      headerComponent={headerContent}
      hasAppBar={
        (displayBars && isExtraSmallViewport) ||
        productStore?.productStorePreferences.visibility ===
          ProductStoreVisibility.Public ||
        productStore?.productStorePreferences.visibility ===
          ProductStoreVisibility.All ||
        productStore?.productStorePreferences.visibility ===
          ProductStoreVisibility.ClientUser
      }
    >
      <PageLayout
        routeAccessCheck="allowClientStore"
        unauthAccessAllowed="allowClientStore"
        fullpage={!isLoggedInUser}
        showSidebar={ShowSidebarConfig.IfUnauthed}
        showSpace={false}
      >
        {pageContent}
      </PageLayout>
    </Layout>
  );
};
